<template>
  <v-container fluid v-if="account">
    <div class="d-flex flex-column flex-md-row justify-space-between">
      <h1>{{ $t('DETAIL_ACCOUNT_PAGE.TITLE.HEADER') }}</h1>

      <div class="d-flex align-center justify-md-center justify-start mt-8 mt-md-0">
        <v-btn class="boom-button-back mr-4" variant="outlined" to="/accounts">
          {{ $t('COMMON.BUTTON.BACK') }}
        </v-btn>

        <v-btn
          v-if="checkPermission(['DISTRIBUTOR_ACCOUNT_UPDATE_PERMISSION'])"
          variant="outlined"
          class="boom-button"
          :to="'/accounts/update/' + `${account?.technical_id}`"
        >
          {{ $t('COMMON.BUTTON.UPDATE') }}
        </v-btn>
      </div>
    </div>

    <v-card class="rounded-xl mt-8" :border="false" :elevation="0">
      <v-row>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.ID') }}</p>
          <p class="font-weight-bold">{{ account?.technical_id }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.EMAIL') }}</p>
          <p class="font-weight-bold">{{ account?.email }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.PHONE_NUMBER') }}</p>
          <p class="font-weight-bold">{{ account?.phone_number }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.STATUS') }}</p>
          <p class="font-weight-bold">
            {{ account ? $t('STATUS.' + getAccountStatusName(account.status)) : '' }}
          </p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.ACTIVATION_DATE') }}</p>
          <p class="font-weight-bold">{{ getFormattedDate(account?.activated_date) }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.ROLE') }}</p>
          <p v-for="role of account?.roles" :key="role.technical_id" class="font-weight-bold">
            {{ role.title }}
          </p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.NETWORK') }}</p>
          <p class="font-weight-bold">{{ account?.network.name }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.AREA') }}</p>
          <p class="font-weight-bold">{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.NOT_SPECIFIED') }}</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.FORMATION_ONE') }}</p>
          <p class="font-weight-bold">{{ account?.formation_one }}</p>
          <p v-if="!account?.formation_one" class="font-weight-bold">-</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.FORMATION_TWO') }}</p>
          <p class="font-weight-bold">{{ account?.formation_two }}</p>
          <p v-if="!account?.formation_two" class="font-weight-bold">-</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.FORMATION_THREE') }}</p>
          <p class="font-weight-bold">{{ account?.formation_three }}</p>
          <p v-if="!account?.formation_three" class="font-weight-bold">-</p>
        </v-col>
        <v-col cols="12" md="3"> </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.DESCRIPTION') }}</p>
          <p class="font-weight-bold">{{ account?.description }}</p>
          <p v-if="!account?.description" class="font-weight-bold">-</p>
        </v-col>
        <v-col cols="12" md="3">
          <p>{{ $t('DETAIL_ACCOUNT_PAGE.LABEL.SUB_STATUS') }}</p>
          <p class="font-weight-bold">{{ account?.sub_status }}</p>
          <p v-if="!account?.sub_status" class="font-weight-bold">-</p>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="rounded-xl mt-8" :border="false" :elevation="0">
      <div class="d-flex flex-column flex-md-row justify-space-between">
        <div :class="{ 'mr-0 mr-md-4': account.has_several_collaborators }" class="w-100">
          <h3 class="font-weight-bold mb-4">
            {{ $t('DETAIL_ACCOUNT_PAGE.TITLE.COLLABORATOR') }}
          </h3>
          <div class="d-flex flex-md-row justify-center align-center">
            <v-text-field
              v-model.trim="search"
              :placeholder="$t('COMMON.FILTER.SEARCH')"
              hide-details
            />
            <v-btn
              v-if="
                account.has_several_collaborators &&
                checkPermission(['DISTRIBUTOR_ADD_COLLABORATOR'])
              "
              class="boom-button ml-4"
              :to="'/accounts/' + `${account.technical_id}` + '/collaborator/new'"
              variant="outlined"
            >
              {{ $t('DETAIL_ACCOUNT_PAGE.BUTTON.NEW_COLLABORATOR') }}
              <img src="@/assets/icons/accounts/cross-hover.svg" alt="icône ajouter" class="pl-2" />
            </v-btn>
          </div>
        </div>
      </div>

      <v-table hover class="mt-8">
        <thead>
          <tr>
            <th id="lastname" @click="sortColumns('lastname')" style="min-width: 200px">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{ $t('DETAIL_ACCOUNT_PAGE.TABLE.LASTNAME') }}</span>
                <SortArrow :order="order" :sort="sort" value="lastname" />
              </div>
            </th>
            <th id="firstname" @click="sortColumns('firstname')" style="min-width: 200px">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{
                  $t('DETAIL_ACCOUNT_PAGE.TABLE.FIRSTNAME')
                }}</span>
                <SortArrow :order="order" :sort="sort" value="firstname" />
              </div>
            </th>
            <th
              id="job_description"
              @click="sortColumns('job_description')"
              style="min-width: 250px"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{
                  $t('DETAIL_ACCOUNT_PAGE.TABLE.JOB_DESCRIPTION')
                }}</span>
                <SortArrow :order="order" :sort="sort" value="job_description" />
              </div>
            </th>
            <th id="actions" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td class="pa-8 pa-md-16 text-md-center" colspan="4">
              {{ $t('DETAIL_ACCOUNT_PAGE.TABLE.LOADING_COLLABORATORS') }}
              <v-progress-linear class="my-5" color="#165039" indeterminate />
            </td>
          </tr>
          <tr v-else-if="!collaborators.length">
            <td class="pa-8 pa-md-16 text-md-center" colspan="4">
              {{ $t('DETAIL_ACCOUNT_PAGE.TABLE.NO_COLLABORATOR') }}
              <div class="d-flex align-center justify-center mt-8">
                <img src="@/assets/images/empty.svg" width="250" alt="Empty" />
              </div>
            </td>
          </tr>
          <tr
            v-for="collaborator of collaborators"
            v-else
            :key="collaborator.technical_id"
            @click.stop.prevent="update(collaborator)"
          >
            <td class="font-weight-bold">
              {{ collaborator.lastname }}
            </td>
            <td class="font-weight-bold">
              {{ collaborator.firstname }}
            </td>
            <td class="color-light">
              {{
                collaborator.job_description
                  ? collaborator.job_description
                  : $t('DETAIL_ACCOUNT_PAGE.LABEL.NOT_SPECIFIED')
              }}
            </td>
            <td
              class="text-right"
              @click.stop.prevent
              v-if="
                checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR']) ||
                checkPermission(['DISTRIBUTOR_DELETE_COLLABORATOR'])
              "
            >
              <v-menu>
                <template v-slot:activator="{ props }">
                  <svg-icon
                    v-bind="props"
                    @click.stop.prevent
                    class="icon"
                    type="mdi"
                    :path="dotVertical"
                  />
                </template>

                <v-list elevation="1">
                  <v-list-item
                    v-if="checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR'])"
                    :to="
                      '/accounts/' +
                      `${account.technical_id}` +
                      '/collaborator/update/' +
                      `${collaborator.technical_id}`
                    "
                    link
                  >
                    <v-list-item-title>
                      {{ $t('COMMON.BUTTON.UPDATE') }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="
                      account.has_several_collaborators &&
                      checkPermission(['DISTRIBUTOR_DELETE_COLLABORATOR']) &&
                      getCurrentCollaborator.technical_id !== collaborator.technical_id &&
                      collaborators.length > 1
                    "
                    link
                    @click="deactivateCollaborator(collaborator)"
                  >
                    <v-list-item-title>
                      {{ $t('COMMON.BUTTON.DELETE') }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </v-table>

      <div v-if="collaborators.length" class="mt-8 d-flex align-center justify-center">
        <p class="load-more color-light" @click="loadMore()">{{ $t('COMMON.BUTTON.LOAD_MORE') }}</p>
      </div>
    </v-card>

    <v-card
      class="rounded-xl mt-8"
      :border="false"
      :elevation="0"
      v-if="account?.group_list.length"
    >
      <h3 class="font-weight-bold mb-4">
        {{ $t('DETAIL_ACCOUNT_PAGE.TITLE.GROUPS') }}
      </h3>

      <v-table :hover="true" class="mt-8">
        <thead>
          <tr>
            <th id="store_name">
              <span class="font-weight-bold">{{ $t('DETAIL_ACCOUNT_PAGE.TABLE.GROUP_NAME') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group of account?.group_list" :key="group.technical_id">
            <td class="font-weight-bold">
              {{ group.name }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-card
      class="rounded-xl mt-8"
      :border="false"
      :elevation="0"
      v-if="account?.store_list.length"
    >
      <h3 class="font-weight-bold mb-4">
        {{ $t('DETAIL_ACCOUNT_PAGE.TITLE.STORES') }}
      </h3>

      <v-table :hover="true" class="mt-8">
        <thead>
          <tr>
            <th id="store_name">
              <span class="font-weight-bold">{{ $t('DETAIL_ACCOUNT_PAGE.TABLE.STORE_NAME') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="store of account?.store_list" :key="store.technical_id">
            <td class="font-weight-bold">
              {{ store.name }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>

  <ConfirmDialogue ref="confirmDialogue" />
</template>

<script>
import AccountService from '@/services/AccountService'
import SvgIcon from '@jamescoyle/vue-icon'
import SortArrow from '@/components/tables/SortArrow.vue'
import { mdiDotsVertical } from '@mdi/js'
import CollaboratorService from '@/services/CollaboratorService'
import AccountStatus from '@/constants/accountStatus'
import ConfirmDialogue from '@/components/modal/ConfirmDialogue.vue'
import { handleResponseError } from '@/components/utils/globalFunction'
import collaboratorStatus from '@/constants/collaboratorStatus'
import { auth as $store } from '@/store/AuthModule'

export default {
  name: 'DetailAccountPage',
  components: { ConfirmDialogue, SortArrow, SvgIcon },
  async created() {
    try {
      this.account = await AccountService.getAccount(this.$route.params.accountId)
      await this.getAccountCollaborator()
    } catch (error) {
      handleResponseError(error)
    }
  },
  data() {
    return {
      account: null,
      dotVertical: mdiDotsVertical,
      loading: false,
      search: '',
      awaitingSearch: false,
      sort: 'lastname',
      order: 'asc',
      page: 1,
      pages: null,
      limit: 12,
      collaborators: []
    }
  },
  watch: {
    search() {
      if (!this.awaitingSearch) {
        setTimeout(() => {
          this.getAccountCollaborator()
          this.awaitingSearch = false
        }, 1000)
      }
      this.awaitingSearch = true
    }
  },
  computed: {
    getCurrentCollaborator() {
      const collaborator = this.$store.getters['auth/getCurrentCollaborator']
      if (collaborator) {
        return collaborator
      } else {
        return ''
      }
    }
  },
  methods: {
    async getAccountCollaborator() {
      this.loading = true
      try {
        const response = await CollaboratorService.getPaginatedAccountCollaboratorsList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit,
          this.account.technical_id
        )
        this.collaborators = response.data
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    update(collaborator) {
      if (this.checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR'])) {
        this.$router.push(
          '/accounts/' +
            this.account.technical_id +
            '/collaborator/update/' +
            collaborator.technical_id
        )
      }
    },
    sortColumns(sortParam) {
      this.page = 1
      this.sort = sortParam
      if (this.order === 'asc') {
        this.order = 'desc'
      } else {
        this.order = 'asc'
      }
      this.getAccountCollaborator()
    },
    checkPermission(permissionsRequired) {
      return this.$store.getters['auth/hasPermission'](permissionsRequired)
    },
    getAccountStatusName(status) {
      return AccountStatus[status].name
    },
    getFormattedDate(date) {
      if (date) {
        return new Date(date).toLocaleDateString()
      } else {
        return '-'
      }
    },
    async loadMore() {
      if (this.page < this.pages) {
        try {
          this.page = this.page + 1
          const response = await CollaboratorService.getPaginatedAccountCollaboratorsList(
            this.search,
            this.sort,
            this.order,
            this.page,
            this.limit,
            this.account.technical_id
          )
          this.collaborators.push(...response.data)
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    },
    async deactivateCollaborator(collaborator) {
      const confirm = await this.$refs.confirmDialogue.show({
        title: this.$t('DETAIL_ACCOUNT_PAGE.MODAL.TITLE'),
        message: this.$t('DETAIL_ACCOUNT_PAGE.MODAL.MESSAGE', {
          collaborator: collaborator.firstname + ' ' + collaborator.lastname
        }),
        warning: this.$t('DETAIL_ACCOUNT_PAGE.MODAL.WARNING'),
        validateButton: this.$t('COMMON.BUTTON.DELETE'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL')
      })
      if (confirm) {
        try {
          const updateCollaborator = {
            firstname: collaborator.firstname,
            technical_id: collaborator.technical_id,
            ...(collaborator.job_description
              ? { job_description: collaborator.job_description }
              : {}),
            lastname: collaborator.lastname,
            status: collaboratorStatus.inactive.name
          }
          const response = await CollaboratorService.updateNetworkCollaborator(
            updateCollaborator,
            this.account.technical_id
          )
          await this.getAccountCollaborator()
          if ($store.state.userDetail.technicalId === this.account.technical_id) {
            await this.$store.commit('auth/updateOneCollaboratorsInList', response)
          }
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.v-container {
  padding: 32px;
}

.v-card {
  padding: 32px;
}

:deep(.v-table__wrapper) {
  border-radius: 20px !important;
}

thead {
  background-color: #165039 !important;
}

th > * {
  color: white;
}

tr:nth-of-type(even) {
  background-color: #fafafa;
}

td {
  border-bottom: unset !important;
}

td:hover {
  cursor: pointer;
}

.color-light {
  color: #bfbfbf;
}

.load-more {
  cursor: pointer;
}
</style>
